import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase/config';
import { CheckCircle, XCircle, Loader2, AlertTriangle, ArrowRight, Lock } from 'lucide-react';
import StudyStayLogo from '../components/StudyStayLogo';

const STATUS = { LOADING: 'loading', SUCCESS: 'success', ERROR: 'error', RESET_FORM: 'reset_form', RESET_SUCCESS: 'reset_success' };

export default function AuthAction() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setStatus(STATUS.ERROR);
      setErrorMessage('Ongeldige link: er ontbreekt een actiecode.');
      return;
    }

    switch (mode) {
      case 'verifyEmail':
        handleVerifyEmail();
        break;
      case 'resetPassword':
        handleVerifyResetCode();
        break;
      default:
        setStatus(STATUS.ERROR);
        setErrorMessage(`Onbekende actie: ${mode || 'geen modus opgegeven'}`);
    }
  }, [mode, oobCode]);

  async function handleVerifyEmail() {
    try {
      await applyActionCode(auth, oobCode);
      console.log('[AuthAction] Email verified successfully');
      setStatus(STATUS.SUCCESS);
    } catch (err) {
      console.error('[AuthAction] verifyEmail error:', err.code, err.message);
      setStatus(STATUS.ERROR);
      setErrorMessage(friendlyError(err));
    }
  }

  async function handleVerifyResetCode() {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode);
      setResetEmail(email);
      setStatus(STATUS.RESET_FORM);
    } catch (err) {
      console.error('[AuthAction] verifyResetCode error:', err.code, err.message);
      setStatus(STATUS.ERROR);
      setErrorMessage(friendlyError(err));
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (newPassword.length < 6) { setErrorMessage('Wachtwoord moet minstens 6 tekens zijn.'); return; }
    if (newPassword !== confirmPw) { setErrorMessage('Wachtwoorden komen niet overeen.'); return; }
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      console.log('[AuthAction] Password reset successfully');
      setStatus(STATUS.RESET_SUCCESS);
    } catch (err) {
      console.error('[AuthAction] resetPassword error:', err.code, err.message);
      setErrorMessage(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  function friendlyError(err) {
    switch (err.code) {
      case 'auth/expired-action-code': return 'Deze link is verlopen. Vraag een nieuwe verificatiemail aan.';
      case 'auth/invalid-action-code': return 'Deze link is ongeldig of werd al gebruikt.';
      case 'auth/user-disabled': return 'Dit account is uitgeschakeld.';
      case 'auth/user-not-found': return 'Geen account gevonden voor deze link.';
      case 'auth/weak-password': return 'Het wachtwoord is te zwak. Gebruik minstens 6 tekens.';
      default: return err.message || 'Er ging iets mis. Probeer het opnieuw.';
    }
  }

  const continueHref = continueUrl || '/';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto shadow-md overflow-hidden" style={{ backgroundColor: '#60ADF0' }}>
              <StudyStayLogo className="w-9 h-9" />
            </div>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200/60 p-8">
          {status === STATUS.LOADING && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-[#5EABEB] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Even geduld...</h2>
              <p className="text-gray-500 text-sm">We verwerken je verzoek.</p>
            </div>
          )}

          {status === STATUS.SUCCESS && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">E-mail geverifieerd!</h2>
              <p className="text-gray-600 mb-6">Je account is bevestigd. Je kunt nu inloggen.</p>
              <Link
                to={continueHref}
                className="inline-flex items-center justify-center gap-2 w-full py-3 px-6 rounded-xl font-semibold text-white transition-colors"
                style={{ backgroundColor: '#5EABEB' }}
              >
                <span>Ga verder</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {status === STATUS.RESET_FORM && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-[#5EABEB]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Nieuw wachtwoord</h2>
                <p className="text-gray-500 text-sm">Stel een nieuw wachtwoord in voor <strong>{resetEmail}</strong></p>
              </div>
              {errorMessage && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{errorMessage}</div>
              )}
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nieuw wachtwoord</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#5EABEB] focus:border-transparent"
                    placeholder="Minstens 6 tekens" required minLength={6} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bevestig wachtwoord</label>
                  <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#5EABEB] focus:border-transparent"
                    placeholder="Herhaal je wachtwoord" required minLength={6} />
                </div>
                <button type="submit" disabled={isSubmitting}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#5EABEB' }}>
                  {isSubmitting ? 'Bezig...' : 'Wachtwoord opslaan'}
                </button>
              </form>
            </div>
          )}

          {status === STATUS.RESET_SUCCESS && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Wachtwoord gewijzigd!</h2>
              <p className="text-gray-600 mb-6">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
              <Link to="/user-type-login"
                className="inline-flex items-center justify-center gap-2 w-full py-3 px-6 rounded-xl font-semibold text-white transition-colors"
                style={{ backgroundColor: '#5EABEB' }}>
                <span>Inloggen</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {status === STATUS.ERROR && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
                {errorMessage.includes('verlopen') ? (
                  <AlertTriangle className="w-9 h-9 text-amber-600" />
                ) : (
                  <XCircle className="w-9 h-9 text-red-600" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Er ging iets mis</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <div className="space-y-3">
                <Link to="/user-type-login"
                  className="inline-flex items-center justify-center gap-2 w-full py-3 px-6 rounded-xl font-semibold text-white transition-colors"
                  style={{ backgroundColor: '#5EABEB' }}>
                  Inloggen en opnieuw proberen
                </Link>
                <Link to="/" className="block text-sm text-gray-500 hover:text-gray-700 transition-colors">
                  Terug naar de startpagina
                </Link>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          &copy; {new Date().getFullYear()} Nolf Technologies OÜ &middot; StudyStay
        </p>
      </div>
    </div>
  );
}
